import { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import type { Preferences, PublicUser } from "../../lib/api";
import {
  markNotificationAsked,
  notificationAsked,
  requestNotifyPermission,
  setNotifyDesktopEnabled,
  setNotifySoundEnabled,
} from "../../lib/notifications";
import { CommandPalette } from "./CommandPalette";

export function AppShell({
  user,
  prefs,
  onPrefs,
  onLogout,
}: {
  user: PublicUser;
  prefs: Preferences | null;
  onPrefs: (p: Preferences) => void;
  onLogout: () => void;
}) {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [notifyBanner, setNotifyBanner] = useState(false);
  const navigate = useNavigate();
  const theme = prefs?.theme || "dawn";

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "?" && e.shiftKey) {
        e.preventDefault();
        setPaletteOpen(true);
      }
      if (e.key === "c" && !e.metaKey && !e.ctrlKey) {
        window.dispatchEvent(new CustomEvent("mailstorm:compose"));
      }
      if (e.key === "/") {
        e.preventDefault();
        setPaletteOpen(true);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (typeof Notification === "undefined") return;
    if (notificationAsked()) return;
    if (Notification.permission === "granted" || Notification.permission === "denied") {
      markNotificationAsked();
      return;
    }
    const t = window.setTimeout(() => setNotifyBanner(true), 1800);
    return () => window.clearTimeout(t);
  }, []);

  const tabs = useMemo(
    () => [
      { to: "/mail", label: "Mail" },
      { to: "/calendar", label: "Calendar" },
      { to: "/contacts", label: "Contacts" },
      { to: "/settings", label: "Settings" },
    ],
    [],
  );

  return (
    <div className="app-shell" data-theme={theme}>
      <header className="topbar">
        <div className="brand">
          <span className="brand-mark">M</span>
          <span className="brand-name">Mailstorm</span>
        </div>
        <nav className="nav-tabs" aria-label="Sezioni">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              className={({ isActive }) => `nav-tab ${isActive ? "active" : ""}`}
            >
              {t.label}
            </NavLink>
          ))}
        </nav>
        <div className="topbar-spacer" />
        <button className="btn search-chip" type="button" onClick={() => setPaletteOpen(true)}>
          <span className="search-chip-ico">⌘K</span>
          Cerca e comandi
        </button>
        <div className="topbar-user">
          <span className="topbar-user-mail">{user.email}</span>
          <button className="btn btn-ghost btn-sm" type="button" onClick={onLogout}>
            Esci
          </button>
        </div>
      </header>
      {notifyBanner && (
        <div className="notify-banner" role="dialog" aria-label="Attiva notifiche">
          <div>
            <strong>Notifiche nuove email</strong>
            <p>
              Attiva le notifiche del PC e il suono di arrivo. I link nelle mail si aprono sempre in una nuova
              scheda.
            </p>
          </div>
          <div className="actions">
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => {
                void (async () => {
                  await requestNotifyPermission();
                  setNotifyDesktopEnabled(true);
                  setNotifySoundEnabled(true);
                  setNotifyBanner(false);
                })();
              }}
            >
              Attiva notifiche
            </button>
            <button
              className="btn"
              type="button"
              onClick={() => {
                markNotificationAsked();
                setNotifyBanner(false);
              }}
            >
              Non ora
            </button>
          </div>
        </div>
      )}
      <Outlet />
      {paletteOpen && (
        <CommandPalette
          onClose={() => setPaletteOpen(false)}
          onNavigate={(path) => {
            setPaletteOpen(false);
            navigate(path);
          }}
          onTheme={(themeName) => {
            if (!prefs) return;
            const next = { ...prefs, theme: themeName };
            onPrefs(next);
            document.documentElement.setAttribute("data-theme", themeName);
          }}
        />
      )}
    </div>
  );
}
